import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import useHomeContext from 'Context/HomeContext';
import fr from 'facet-rest';

const NewLoginContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const UPDATE_COLLECTION = 'update-collection';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    isSubmitting: false,
    submittingError: null,
    authCollection: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        authCollection: action.authCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case UPDATE_COLLECTION: {
      return {
        ...state,
        authCollection: action.authCollection,
      };
    }
    default: {
      throw new Error(`Unhandled NewLoginContext action type: ${action.type}`);
    }
  }
};

const NewLoginProvider = ({
  children,
}) => {
  const { refreshHomeResource } = useHomeContext();

  const [state, dispatch] = React.useReducer(reducer, null, init);

  const handleSubmit = React.useCallback(async (coll) => {
    dispatch({ type: UPDATE_COLLECTION, authCollection: coll });
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      isSubmitting: state.isSubmitting,
      submittingError: state.submittingError,
      authCollection: state.authCollection,
      handleSubmit,
    };
  }, [state, handleSubmit]);

  useComponentDidMount(async () => {
    try {
      const updatedHomeResource = await refreshHomeResource({ skipFetchIfRecentlyUpdated: true });
      let relationship = fr.relationships.login.name;
      if (!updatedHomeResource) {
        throw new Error('Failed to connect. We\'ll be back soon!');
      } else if (updatedHomeResource.relationship(fr.relationships.user.name)) {
        relationship = fr.relationships.user.name;
      }

      dispatch({ type: INITIALIZE_STARTED });
      const coll = await updatedHomeResource.relationship(relationship).link('related').get();
      dispatch({
        type: INITIALIZE_SUCCEEDED,
        authCollection: coll,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  });

  return (
    <NewLoginContext.Provider value={memoizedValue}>
      {children}
    </NewLoginContext.Provider>
  );
};

// Use Survey State Context
const useNewLoginContext = () => {
  const context = React.useContext(NewLoginContext);
  if (context === undefined) {
    throw new Error('useNewLoginContext must be used within a NewLoginProvider');
  }
  return context;
};

NewLoginProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  NewLoginProvider,
  useNewLoginContext,
};
